import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CalendarX2, CalendarCheck2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Skeleton } from "@/components/ui/skeleton";

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function to12h(t) {
  if (!t) return "";
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${mStr} ${ampm}`;
}

function isOutsideSeason(d) {
  const y = d.getFullYear();
  return d < new Date(y, 5, 18) || d > new Date(y, 8, 6);
}

export default function AdminCalendar() {
  const [closed, setClosed] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [fullyBooked, setFullyBooked] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const thisMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);

  const load = async () => {
    setLoading(true);
    try {
      const [c, b, avail] = await Promise.all([
        api.get("/admin/closed-dates"),
        api.get("/admin/bookings"),
        api.get("/availability"),
      ]);
      setClosed(c.data.closed_dates || []);
      setBookings(b.data.bookings || []);
      setFullyBooked(avail.data.fully_blocked_dates || []);
    } catch {
      toast.error("Failed to load calendar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async () => {
    if (!selected) return;
    const ds = fmtDate(selected);
    if (isOutsideSeason(selected)) return toast.error("Outside open season — already unavailable");
    setActing(true);
    try {
      if (closed.includes(ds)) {
        await api.delete(`/admin/closed-dates/${ds}`);
        toast.success("Date reopened");
      } else {
        await api.post("/admin/closed-dates", { date: ds });
        toast.success("Date closed");
      }
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Action failed"));
    } finally {
      setActing(false);
    }
  };

  const selectedStr = selected ? fmtDate(selected) : null;
  const dayBookings = bookings.filter(
    (b) => selectedStr && b.appointment_date === selectedStr && b.status !== "cancelled"
  );
  const isClosed = selectedStr && closed.includes(selectedStr);
  const outsideSeason = selected && isOutsideSeason(selected);
  const isFullyBooked = selectedStr && fullyBooked.includes(selectedStr);

  return (
    <div data-testid="admin-calendar">
      <p className="overline mb-2">Calendar</p>
      <h1 className="font-serif text-4xl mb-2">Manage availability</h1>
      <p className="text-white/55 mb-8 text-sm max-w-2xl">
        By default, every day outside June 18 – Sept 6 is unavailable. Tap any date in season
        to close it (e.g. personal day, weather). Clients won't be able to book closed days.
      </p>

      {loading ? (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="card-luxe p-4">
            <Skeleton className="h-8 w-1/2 mb-4" />
            <div className="grid grid-cols-7 gap-2">
              {Array.from({ length: 35 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          </div>
          <div className="card-luxe p-6">
            <Skeleton className="h-4 w-1/3 mb-3" />
            <Skeleton className="h-8 w-2/3 mb-4" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="card-luxe p-4">
            <Calendar
              mode="single"
              selected={selected ?? undefined}
              onSelect={(d) => setSelected(d ?? null)}
              defaultMonth={thisMonth}
              modifiers={{
                closed:         (d) => closed.includes(fmtDate(d)),
                outside_season: (d) => isOutsideSeason(d),
                has_booking:    (d) => bookings.some((b) => b.appointment_date === fmtDate(d) && b.status !== "cancelled"),
                fully_booked:   (d) => fullyBooked.includes(fmtDate(d)),
              }}
              modifiersClassNames={{
                closed:         "rdp-closed",
                outside_season: "rdp-outside-season",
                has_booking:    "rdp-has-booking",
                fully_booked:   "rdp-fully-booked",
              }}
              data-testid="admin-calendar-picker"
            />
            <div className="flex flex-wrap gap-3 mt-4 text-[11px] tracking-wide">
              <span className="inline-flex items-center gap-1.5 text-white/60">
                <span className="w-2 h-2" style={{ background: "#8A8A8A" }} /> Booked
              </span>
              <span className="inline-flex items-center gap-1.5 text-white/60">
                <span className="w-2 h-2" style={{ background: "rgba(255,152,0,0.5)" }} /> Fully booked
              </span>
              <span className="inline-flex items-center gap-1.5 text-white/60">
                <span className="w-2 h-2" style={{ background: "rgba(244,67,54,0.5)" }} /> Closed
              </span>
              <span className="inline-flex items-center gap-1.5 text-white/60">
                <span className="w-2 h-2 bg-white/20" /> Out of season
              </span>
            </div>
          </div>

          <div className="card-luxe p-6">
            {!selected ? (
              <p className="text-white/40 text-sm">Click a date to see details.</p>
            ) : (
              <>
                <p className="overline mb-3">Selected · {selectedStr}</p>
                <h3 className="font-serif text-2xl mb-4">
                  {outsideSeason
                    ? "Outside open season"
                    : isFullyBooked
                    ? "Fully booked"
                    : isClosed
                    ? "Closed for bookings"
                    : "Open"}
                </h3>

                {!outsideSeason && (
                  <button
                    className="btn-secondary w-full justify-center mb-6"
                    onClick={toggle}
                    disabled={acting}
                    data-testid="admin-toggle-closed"
                  >
                    {acting ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : isClosed ? (
                      <CalendarCheck2 size={14} />
                    ) : (
                      <CalendarX2 size={14} />
                    )}
                    {isClosed ? "Reopen This Date" : "Close This Date"}
                  </button>
                )}

                <div className="spec-divider mb-5" />
                <p className="overline mb-3">Bookings on this day</p>
                {dayBookings.length === 0 ? (
                  <p className="text-white/40 text-sm">No bookings.</p>
                ) : (
                  <ul className="space-y-2" data-testid="admin-day-bookings">
                    {dayBookings.map((b) => (
                      <li key={b.id} className="p-3 text-sm flex items-center justify-between">
                        <div>
                          <p className="font-mono text-gold">{b.id}</p>
                          <p className="text-white/70 text-xs">{b.contact?.name} · {to12h(b.appointment_time)}</p>
                          <p className="text-white/50 text-xs">{b.car?.make} {b.car?.model}</p>
                        </div>
                        <span className="text-xs text-white/40 capitalize">{b.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
